import { Button } from "antd";
import { Form, Formik } from "formik";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import useGetData from "../../api/useGetData";
import SelectInput from "../../common/SelectInput";
import { isPointInPolygon } from "../../utils/mapUtils";
import { AirportRideDetailsValidation } from "../../utils/validations";
import MapModal from "../MapModal/MapModal";
import AgentUserSelect from "../UsersSelect";
import { AirportRideContext } from "./AirportRideContext";
import { bookingTracking } from "../../utils/mixpanel";

/**
 * The Ride details step of the airport ride trip
 *
 * It shows a form containing the following:
 *  - In the case of agent, the user whom the trip is intended for
 *  - The ride type (pick up / drop off)
 *  - The city the trip is intended to be in. The options for this comes from backend.
 *  - The airport name. The options for this comes from backend depnding on the selected city.
 *  - The terminal number of the airpot. The options for this comes from backend depnding on the selected city and airport.
 *  - The location/destination on map. it operates depending on certain allowed zones that come from the backend. Whether the selected location on map is intended as pick up or drop off depend on the selected ride type
 *
 * @returns {JSX.Element}
 */

const RideDetailsStep = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation("global");
  const resetRef = useRef();
  const [mapModalOpen, setMapModalOpoen] = useState(false);
  const { setActiveTab, firstStepValues, setFirstStepValues } =
    useContext(AirportRideContext);
  const user = useSelector((state) => state.authData.user);
  const rideTypeOptions = [
    { value: t("hero.pickup_value_text"), label: t("hero.pickup_text") },
    { value: t("hero.dropoff_value_text"), label: t("hero.dropoff_text") },
  ];
  const {
    data: cities,
    getData: getCitiesData,
    loading: gettingCities,
  } = useGetData({
    route: "api/method/airport_transport.api.bookings.get_city",
    params: {
      service: "Airport Trip",
    },
  });
  const { getData: getAirports, data: airports } = useGetData({
    route: "api/method/airport_transport.api.bookings.get_airport",
    notLoadData: true,
  });
  const getTerminals = (selectedAirport) => {
    return airports?.find(({ airport }) => airport === selectedAirport)
      ?.terminals;
  };
  const { data: zonesData, getData: getZonesData } = useGetData({
    route: "api/method/airport_transport.api.bookings.get_zones",
    notLoadData: true,
  });
  useEffect(() => {
    if (firstStepValues.arrivalCity) {
      getZonesData({
        params: {
          city: firstStepValues.arrivalCity,
          service: "Airport Trip",
        },
      });
    }
  }, [firstStepValues]);

  useEffect(() => {
    if (!gettingCities) {
      getCitiesData({
        params: {
          service: "Airport Trip",
        },
      });
    }
    if (resetRef.current) {
      resetRef.current.click();
    }
  }, [language]);
  const resetPickup = (setFieldValue) => {
    setFieldValue("location", "");
    setFieldValue("locationCoords", null);
  };
  const resetDropoff = (setFieldValue) => {
    setFieldValue("destination", "");
    setFieldValue("destinationCoords", null);
  };
  const onSubmit = (values) => {
    const zone = (
      zonesData.find((zone) =>
        isPointInPolygon(values.destinationCoords, zone.map)
      ) || zonesData?.[0]
    )?.name;
    setFirstStepValues({
      agentUser: values.agentUser,
      rideType: values.rideType,
      arrivalCity: values.arrivalCity,
      airportName: values.airportName,
      terminalNumber: values.terminalNumber,
      location: values.location,
      destination: values.destination,
      locationCoords: values.locationCoords,
      destinationCoords: values.destinationCoords,
      zone,
    });
    bookingTracking({
      user: user?.email,
      service: "Airport",
      step: "Ride Details Step",
    });
    setActiveTab(2);
  };

  return (
    <div>
      <Formik
        initialValues={{
          agentUser: firstStepValues.agentUser,
          rideType: firstStepValues.rideType,
          arrivalCity: firstStepValues.arrivalCity,
          airportName: firstStepValues.airportName,
          terminalNumber: firstStepValues.terminalNumber,
          location: firstStepValues.location,
          destination: firstStepValues.destination,
          locationCoords: firstStepValues.locationCoords,
          destinationCoords: firstStepValues.destinationCoords,
        }}
        onSubmit={onSubmit}
        validationSchema={AirportRideDetailsValidation(user?.agent)}
      >
        {({
          setFieldValue,
          values,
          errors,
          touched,
          setTouched,
          resetForm,
        }) => (
          <Form className="flex flex-col gap-4">
            <input className="hidden" onClick={resetForm} ref={resetRef} />
            {user?.agent && (
              <AgentUserSelect
                label={t("user_text")}
                name={"agentUser"}
                onChange={(user) => {
                  setFieldValue("agentUser", user);
                }}
                placeholder={t("hero.select_user")}
              />
            )}
            <SelectInput
              name="rideType"
              label={t("hero.ride_type_text")}
              placeholder={t("hero.select_ride_type")}
              options={rideTypeOptions}
              onChange={(type) => {
                resetPickup(setFieldValue);
                resetDropoff(setFieldValue);
                setFieldValue("rideType", type);
              }}
            />
            <SelectInput
              name="arrivalCity"
              label={t("hero.arrival_city_text")}
              placeholder={t("hero.select_arrival_city")}
              options={cities?.map((c) => ({ value: c, label: c }))}
              onChange={(city) => {
                getAirports({
                  params: {
                    city: city,
                  },
                });
                getZonesData({
                  params: {
                    city,
                    service: "Airport Trip",
                  },
                });
                setFieldValue("terminalNumber", "");
                setFieldValue("location", "");
                resetPickup(setFieldValue);
                resetDropoff(setFieldValue);
                setFieldValue("airportName", "");
                setFieldValue("arrivalCity", city);
              }}
            />
            <SelectInput
              name="airportName"
              label={t("hero.airport_name_text")}
              placeholder={t("hero.select_airport_name")}
              options={airports?.map(({ airport }) => ({
                value: airport,
                label: airport,
              }))}
              onChange={(airport) => {
                resetPickup(setFieldValue);
                resetDropoff(setFieldValue);
                setFieldValue("airportName", airport);
              }}
            />
            <SelectInput
              name="terminalNumber"
              label={t("hero.terminal_number_text")}
              placeholder={t("hero.select_terminal_number")}
              options={getTerminals(values.airportName)?.map(
                ({ terminal }) => ({
                  value: terminal,
                  label: terminal,
                })
              )}
              onChange={(terminal) => {
                resetPickup(setFieldValue);
                resetDropoff(setFieldValue);
                setFieldValue("terminalNumber", terminal);
              }}
            />
            <div className="flex flex-col gap-1">
              <button
                onClick={() => {
                  setMapModalOpoen(true);
                  setTouched("location", true);
                  setTouched("destination", true);
                }}
                className="flex justify-between w-full bg-background_white text-gray-700 border border-border_color font-medium rounded-lg text-sm px-3 py-3 text-left disabled:bg-background_grey"
                type="button"
                disabled={
                  !(
                    values.rideType &&
                    values.arrivalCity &&
                    values.airportName &&
                    values.terminalNumber
                  )
                }
              >
                <span>{t("hero.place_of_arrival_text")}</span>
                <img src="./assets/map/map.png" alt="map" />
              </button>
              {touched.location &&
                touched.destination &&
                (errors.location || errors.destination) && (
                  <span
                    style={{
                      color: "#ff4d4f",
                      fontSize: "small",
                    }}
                  >
                    {t(errors.location) || t(errors.destination)}
                  </span>
                )}
            </div>
            {mapModalOpen && (
              <MapModal
                cityName={values.arrivalCity}
                closeModal={() => {
                  setMapModalOpoen(false);
                }}
                rideName="airportRide"
                isModalOpen={mapModalOpen}
                formValues={values}
                confirm={({
                  location,
                  locationCoords,
                  destination,
                  destinationCoords,
                }) => {
                  setFieldValue("location", location);
                  setFieldValue("locationCoords", locationCoords);
                  setFieldValue("destination", destination);
                  setFieldValue("destinationCoords", destinationCoords);
                }}
                zoneCoords={zonesData}
              />
            )}
            <Button
              className="bg-background_steel_blue w-full text-text_white hover:bg-gray-100 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 mt-3"
              type="button"
              htmlType="submit"
              disabled={mapModalOpen}
            >
              {t("next_text")}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RideDetailsStep;
